import { ADMIN_GROUP_SUFFIX } from '@upright/common/iam/access/user-groups/api-types';
import { some, sortBy } from 'lodash';
import {
  nonOrganizationalUserGroupIdsWithUsers,
  UserGroupPlain,
  userGroupsWithOrganizationLinkingDisabled,
} from './types';

type UserGroup<T extends UserGroup<T>> = {
  id: number;
  parents?: T[];
};

/**
 * Takes an user group with recursive parents, returns an array with references to all ancestor
 * groups in the whole recursive tree. Meant to ease operations done with all ancestors.
 *
 * Note that since groups are taken only from references, the 'everyone' special user group
 * is missing from the return value. The upside is that the return type is the same as input.
 */
export function getAllAncestorsOfUserGroup<T extends UserGroup<T>>(userGroup: T): T[] {
  const ancestors: T[] = [];
  function recursion(subUserGroup: T) {
    // If current group is not yet found from ancestors, add it
    if (!some(ancestors, ancestor => ancestor.id === subUserGroup.id)) {
      ancestors.push(subUserGroup);
    }
    // Recurse on all parents, if any
    if (subUserGroup.parents) {
      for (const parent of subUserGroup.parents) {
        recursion(parent);
      }
    }
  }
  // Run the recursion
  recursion(userGroup);
  return ancestors;
}

/**
 * Picks the single group that best identifies which part of an organization a user belongs to.
 * Intended for analytics, where only one scalar value per user can be reported.
 *
 * The user's own memberships are already the most specific level available — anything more
 * general is only reachable through `parents` — so the choice is between them:
 *
 * - Non-organizational groups are never candidates; they describe licences and features rather
 *   than who the user is.
 * - A group without an `organization` is an organizational sub-group and says more about the
 *   user than the organization's own root group, so it wins.
 * - Ties are broken by the lowest id, because `groups` has no guaranteed order and the value
 *   must stay stable from session to session.
 */
export function getMostSpecificUserGroup<T extends { id: number; organization?: unknown }>(groups: T[]): T | undefined {
  const organizationalGroups = sortBy(
    groups.filter(
      group =>
        !userGroupsWithOrganizationLinkingDisabled.includes(group.id) &&
        !nonOrganizationalUserGroupIdsWithUsers.includes(group.id),
    ),
    group => group.id,
  );
  return organizationalGroups.find(group => !group.organization) ?? organizationalGroups[0];
}

export type Role = 'ADMIN' | 'MEMBER';

export function getRoleFromUserGroup(userGroup: UserGroupPlain): Role {
  if (userGroup.name.endsWith(ADMIN_GROUP_SUFFIX)) {
    return 'ADMIN';
  }
  return 'MEMBER';
}

export function isAdminUserGroup(userGroup: UserGroupPlain): boolean {
  return userGroup.name.endsWith(ADMIN_GROUP_SUFFIX);
}

export function isMemberUserGroup(userGroup: UserGroupPlain): boolean {
  return !userGroup.name.endsWith(ADMIN_GROUP_SUFFIX);
}
